import { cn } from "cn";
import { type ComponentProps, useEffect, useRef } from "react";

import { createRecordingWaveSurfer } from "../_libs/recording-wavesurfer";

type MicStreamWaveformProps = Omit<ComponentProps<"div">, "children"> & {
  /** The open microphone stream to paint. `null` paints nothing. */
  stream: MediaStream | null;
  height?: number;
};

/**
 * A scrolling sound-wave for a microphone stream that SOMEONE ELSE is recording — the
 * Birdie composer's voice memo, whose `MediaRecorder` also feeds the live-caption
 * socket, so a second mic capture (what the legacy recorder popover does) is not an
 * option. WaveSurfer's record plugin can render an externally-owned stream through an
 * analyser without touching the stream's tracks; it is torn down with the stream.
 */
export function MicStreamWaveform({
  stream,
  height,
  className,
  ...props
}: MicStreamWaveformProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !stream) return;
    const { wavesurfer, record } = createRecordingWaveSurfer(container, {
      height,
    });
    const micStream = record.renderMicStream(stream);
    return () => {
      micStream.onDestroy();
      wavesurfer.destroy();
    };
  }, [stream, height]);

  return (
    <div
      data-slot="mic-stream-waveform"
      ref={containerRef}
      className={cn("waveform w-full relative overflow-hidden", className)}
      {...props}
    />
  );
}
